import React, {
  ChangeEvent,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState
} from 'react';
import { IFilterParams, ValueFormatterFunc } from 'ag-grid-community';
import { Box, Select } from '@chakra-ui/react';

export const SELECT_FILTER_TYPE = 'equals';

/**
 * A custom FilterComponent that represents the filter values as a select list
 */
// eslint-disable-next-line react/display-name
const SelectFilter = forwardRef((props: ISelectionFilterParams, ref) => {
  const [filterValue, setFilterValue] = useState('');

  // expose AG Grid Filter Lifecycle callbacks
  useImperativeHandle(ref, () => {
    return {
      doesFilterPass() {
        // (params: IDoesFilterPassParams)
        return true;
      },

      isFilterActive() {
        return filterValue !== undefined && filterValue !== '';
      },

      getModel() {
        return {
          values: [filterValue],
          filterType: SELECT_FILTER_TYPE
        };
      },

      setModel(model: { vals: string[] }) {
        let val;
        if (model) {
          const { vals } = model;
          // we only take one value, since this is a single selection filter
          if (vals.length) {
            val = vals[0];
          }
        }
        if (val === undefined) {
          setFilterValue('');
        } else {
          setFilterValue(val);
        }
      }
    };
  });

  function handleValueChange(evt: ChangeEvent<HTMLSelectElement>) {
    const val = evt.currentTarget.value;
    setFilterValue(val);
  }

  useEffect(() => {
    props.filterChangedCallback();
  }, [props, filterValue]);

  const { values, valueFormatter } = props;

  // the SelectionFilter checks for a valueFormatter function. If one is provided, it will use that to format the
  // values
  const options = values.map((val) => {
    return {
      value: val,
      label: valueFormatter
        ? valueFormatter({
            api: props.api,
            colDef: props.colDef,
            column: props.column,
            columnApi: props.columnApi,
            context: props.context,
            data: undefined,
            node: null,
            value: val
          })
        : val
    };
  });

  return (
    <Box className={'SelectionFilter'}>
      <Select value={filterValue} onChange={handleValueChange}>
        <option value={''}>- All -</option>
        {options.map((opt, i) => (
          <option key={i} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </Select>
    </Box>
  );
});
export default SelectFilter;

interface ISelectionFilterParams extends IFilterParams {
  values: string[];
  valueFormatter?: ValueFormatterFunc;
}
